/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import { FieldLabel, TextField } from 't3-widgets'

import { VALIDATORS_CONFIG, MOCK_TEST_DATA, TEST_DATA } from '../../../../../../common/constants'
import { setActivationCode, setCustomerNumber } from '../../../../../../redux/actions'
import {
    validationChecker,
    verifyCustomer,
    loadContent,
    loadingProcess,
    getTooltip,
    getValidator
} from '../../../common/operations'

import styles from '../pages.module.css'

const CustomerPage = ({ activeStep, fetchTranslateSuccess, intl, setPagerFunctions, setStep }) => {
    const dispatch = useDispatch()

    const activationCodeRedux = useSelector(state => state.activationCode.activationCode) || ''
    const customerNumberRedux = useSelector(state => state.customerNumber.customerNumber) || ''
    const activationCodeTextField = useRef()
    const customerNumberTextField = useRef()

    const [fetchProcessSuccess, setFetchProcessSuccess] = useState(true)
    const [state, setStateOfProperty] = useState({
        activationCode: MOCK_TEST_DATA ? TEST_DATA.step1.activationCode : activationCodeRedux,
        customerNumber: MOCK_TEST_DATA ? TEST_DATA.step1.customerNumber : customerNumberRedux,
        errorActivationCode: false,
        errrorCustomerNumber: false,
        serverErrorMessage: ''
    })

    const setState = statesArr => {
        setStateOfProperty(prevState => ({ ...prevState, ...statesArr }))
    }

    const getValidators = () => {
        const conf = VALIDATORS_CONFIG.step1

        const VALIDATORS = {
            activationCodeCorrectness: getValidator(
                conf.activationCode.enabled.correctness,
                conf.activationCode.regex.test(state.activationCode) &&
                    state.activationCode.replace(' ', '').length === 10
            ),
            activationCodeMaxLenght: getValidator(
                conf.activationCode.enabled.maxLength,
                state.activationCode.replace(' ', '').length > 10
            ),
            activationCodeMinLength: getValidator(
                conf.activationCode.enabled.minLength,
                state.activationCode.replace(' ', '').length < 10
            ),
            customerNumberCorrectness: getValidator(
                conf.customerNumber.enabled.correctness,
                state.customerNumber.length >= 4 && state.customerNumber.length <= 10 && !isNaN(state.customerNumber)
            ),
            customerNumberMaxLength: getValidator(
                conf.customerNumber.enabled.maxLength,
                state.customerNumber.length > 10
            ),
            customerNumberMinLength: getValidator(
                conf.customerNumber.enabled.minLength,
                state.customerNumber.length < 4
            )
        }

        return {
            ...VALIDATORS,
            ...{ allDataValid: VALIDATORS.activationCodeCorrectness && VALIDATORS.customerNumberCorrectness }
        }
    }

    const onServerError = response => {
        let errorMessage = ''
        if (response.status === 403) {
            if (response.data.message.errorCode === 'err_unknown_resource_customer_no') {
                errorMessage =
                    intl.translate('ap_step1_error_customernumber') +
                    ' ' +
                    intl.translate('ap_generic_customersupport_plain')
            } else {
                errorMessage =
                    intl.translate('ap_step1_error_cak') + ' ' + intl.translate('ap_generic_customersupport_plain')
            }
        } else {
            errorMessage = intl.translate('ap_generic_error_text_1')
        }
        setState({ serverErrorMessage: errorMessage })
    }

    const onServerSuccess = () => {
        const activationCode = state.activationCode.replace(' ', '')
        const activationCodeFormatted = activationCode.slice(0, 5) + ' ' + activationCode.slice(5)

        dispatch(setActivationCode(activationCodeFormatted))
        dispatch(setCustomerNumber(state.customerNumber))
        setState({ serverErrorMessage: '' })
        setStep(activeStep + 1)
    }

    const onNext = () => {
        const activationCode = state.activationCode.replace(' ', '')
        const activationCodeFormatted = activationCode.slice(0, 5) + ' ' + activationCode.slice(5)

        setFetchProcessSuccess(false)
        verifyCustomer(activationCodeFormatted, state.customerNumber)
            .then(() => {
                setFetchProcessSuccess(true)
                onServerSuccess()
            })
            .catch(response => {
                setFetchProcessSuccess(true)
                onServerError(response)
            })
    }

    useEffect(() => {
        setPagerFunctions({
            onBack: () => setStep(activeStep - 1),
            onNext: () => onNext(),
            validator: getValidators().allDataValid
        })
    }, [state.customerNumber, state.activationCode])

    const validActivationCode = () => {
        setState({ errorActivationCode: false })

        const activationCodeRegex = VALIDATORS_CONFIG.step1.activationCode.regex

        if (getValidators().activationCodeMinLength) {
            setState({ errorActivationCode: new Error(intl.translate('ap_error_minlength', 10)) })
        } else if (getValidators().activationCodeMaxLength) {
            setState({ errorActivationCode: new Error(intl.translate('ap_error_maxlength', 10)) })
        } else if (state.activationCode.replace(' ', '').length === 10) {
            if (!activationCodeRegex.test(state.activationCode)) {
                setState({ errorActivationCode: new Error(intl.translate('ap_step1_error_cak')) })
            }
        }
    }

    const isValidActivationCode = () => {
        if (typeof state.activationCode === 'undefined') return false
        return getValidators().activationCodeCorrectness
    }

    const activationCodeInput = () => {
        const conf = VALIDATORS_CONFIG.step1

        return (
            <div className={styles['input-field']}>
                <FieldLabel
                    id="activation-code-label"
                    label={intl.translate('ap_step1_cak_label')}
                    tooltip={getTooltip(intl.translate('ap_step1_cak_label'), intl.translate('ap_step1_cak_hint'))}
                >
                    <div className={styles['validated-input-container']}>
                        <div className={styles['validated-input-field']}>
                            <TextField
                                id="activation-code-text-field"
                                ref={activationCodeTextField}
                                className={styles['text-input']}
                                value={state.activationCode}
                                onChange={e => setState({ activationCode: e.target.value.toUpperCase() })}
                                onBlur={() =>
                                    getValidator(conf.activationCode.enabled.correctness, validActivationCode(), null)
                                }
                                onFocus={() => setState({ errorActivationCode: false })}
                                error={state.errorActivationCode}
                                placeholder={intl.translate('wf_reporting_required')}
                            />
                        </div>
                        {validationChecker(isValidActivationCode())}
                    </div>
                </FieldLabel>
            </div>
        )
    }

    const validCustomerNumber = () => {
        setState({ errorCustomerNumber: false })

        if (!isNaN(state.customerNumber)) {
            if (getValidators().customerNumberMinLength) {
                setState({ errorCustomerNumber: new Error(intl.translate('ap_error_minlength', 4)) })
            } else if (getValidators().customerNumberMaxLength) {
                setState({ errorCustomerNumber: new Error(intl.translate('ap_error_maxlength', 10)) })
            }
        } else {
            setState({ errorCustomerNumber: new Error(intl.translate('ap_error_digits')) })
        }
    }

    const customerNumberInput = () => {
        const conf = VALIDATORS_CONFIG.step1

        return (
            <div className={styles['input-field']}>
                <FieldLabel
                    id="customer-number-label"
                    label={intl.translate('ap_step1_customernumber_label')}
                    tooltip={getTooltip(
                        intl.translate('ap_step1_customernumber_label'),
                        intl.translate('ap_step1_customernumber_hint')
                    )}
                >
                    <div className={styles['validated-input-container']}>
                        <div className={styles['validated-input-field']}>
                            <TextField
                                id="customer-number-text-field"
                                ref={customerNumberTextField}
                                className={styles['text-input']}
                                value={state.customerNumber}
                                onChange={e => setState({ customerNumber: e.target.value })}
                                onBlur={() =>
                                    getValidator(conf.customerNumber.enabled.correctness, validCustomerNumber(), null)
                                }
                                onFocus={() => setState({ errorCustomerNumber: false })}
                                error={state.errorCustomerNumber}
                                placeholder={intl.translate('wf_reporting_required')}
                            />
                        </div>
                        {validationChecker(getValidators().customerNumberCorrectness)}
                    </div>
                </FieldLabel>
            </div>
        )
    }

    const pageContent = (
        <div>
            <div className={styles['page-description']}>
                <span className={styles['step-title']}>{intl.translate('ap_step1_title')}</span>
                <p>{intl.translate('ap_step1_para')}</p>
                {loadingProcess(fetchProcessSuccess)}
            </div>
            {activationCodeInput()}
            {customerNumberInput()}
            <div className={styles['error-message']}>{state.serverErrorMessage}</div>
        </div>
    )

    return <div className={styles['content-wrapper']}>{loadContent(fetchTranslateSuccess, pageContent)}</div>
}

CustomerPage.propTypes = {
    activeStep: PropTypes.number,
    fetchTranslateSuccess: PropTypes.boolean,
    intl: PropTypes.objectOf(PropTypes.func),
    setPagerFunctions: PropTypes.func,
    setStep: PropTypes.func
}

export { CustomerPage }
